<script setup lang="ts">
import { computed } from 'vue'
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { useFloatingPanel } from '../../composables/useFloatingPanel'
import FlowReadonlyCanvas from '../FlowReadonlyCanvas.vue'

/** Floating read-only flow chart highlighting the current node ('perlingo.flow-canvas'). */
const player = useDeckPlayerContext()
const { showFlowCanvas, closeFlowCanvas } = player
const { cursorNodeId: graphCursorNodeId } = player.state
const canvasFlowNodes = computed(() => player.graph.value?.nodes ?? [])
const canvasFlowEdges = computed(() => player.graph.value?.edges ?? [])

// Half the viewport by default: room for the active node and its neighbours.
const {
  panelRef: cvsPanelRef,
  style: cvsPanelStyle,
  startDrag: startCvsDrag,
  startResize: startCvsResize,
} = useFloatingPanel({
  position: { left: 20, top: 40 },
  size: {
    width: Math.min(550, Math.max(420, (window.innerWidth - 80) / 2)),
    height: Math.min(380, Math.max(320, (window.innerHeight - 140) / 2)),
  },
  minSize: { width: 320, height: 200 },
})
</script>

<template>
  <Teleport to="body">
    <div v-if="showFlowCanvas" ref="cvsPanelRef" class="pfc-cvsf-float" :style="cvsPanelStyle">
      <div class="pfc-cvsf-header" @mousedown="startCvsDrag">
        <span class="pfc-cvsf-title">Flow Canvas</span>
        <button class="pfc-cvsf-close" type="button" @mousedown.stop @click="closeFlowCanvas">
          ✕
        </button>
      </div>
      <div class="pfc-cvsf-body">
        <FlowReadonlyCanvas
          :nodes="canvasFlowNodes"
          :edges="canvasFlowEdges"
          :active-node-id="graphCursorNodeId"
        />
      </div>
      <div class="pfc-panel-resize-handle" @mousedown="startCvsResize" />
    </div>
  </Teleport>
</template>

<style scoped src="./styles/floatingPanel.css"></style>

<style scoped>
/* ── Flow canvas floating panel ─────────────────────────────────────────────── */
.pfc-cvsf-float {
  position: fixed;
  z-index: 999998;
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 0.72rem;
  min-width: 320px;
  min-height: 200px;
}

.pfc-cvsf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  background: #1e293b;
  border-bottom: 1px solid #334155;
  cursor: grab;
  flex-shrink: 0;
}

.pfc-cvsf-header:active {
  cursor: grabbing;
}

.pfc-cvsf-title {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.pfc-cvsf-close {
  all: unset;
  cursor: pointer;
  color: #64748b;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.1rem 0.2rem;
  border-radius: 3px;
}
.pfc-cvsf-close:hover {
  color: #f8fafc;
  background: #334155;
}

.pfc-cvsf-body {
  flex: 1;
  min-height: 0;
  position: relative;
}
</style>
